import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Tooltip
} from '@mui/material';
import { 
  Camera, 
  Upload, 
  Trash2, 
  Eye, 
  Calendar,
  Plus,
  Image as ImageIcon,
  Download,
  GitCompare as Compare
} from 'lucide-react';
import { PhotoEntry, DatabaseSchema } from '../types';
import { format } from 'date-fns';
import { uploadToBlob, deleteFromBlob } from '../utils/blobStorage';

interface PhotoProgressTrackerProps {
  database: DatabaseSchema | null;
  onAddPhoto: (photo: PhotoEntry) => Promise<void>;
  onDeletePhoto?: (id: string) => Promise<void>;
}

export const PhotoProgressTracker: React.FC<PhotoProgressTrackerProps> = ({ 
  database, 
  onAddPhoto,
  onDeletePhoto 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [category, setCategory] = useState<'front' | 'side' | 'back' | 'other'>('front');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoEntry | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [comparePhotos, setComparePhotos] = useState<PhotoEntry[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photos = database?.photos || [];
  const sortedPhotos = [...photos].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
      } else {
        alert('Please select an image file.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) return;

    setLoading(true);
    
    try {
      // Create photo metadata first
      const photoId = `photo_${Date.now()}`;
      const photoDate = format(new Date(), 'yyyy-MM-dd');
      
      // Upload to Blob storage with structured filename
      const imageUrl = await uploadToBlob(selectedFile, {
        id: photoId,
        date: photoDate,
        category
      });
        
      const newPhoto: PhotoEntry = {
        id: photoId,
        date: photoDate,
        category,
        imageUrl: imageUrl, // Store blob URL in imageUrl field
        notes: notes || undefined,
      };

      await onAddPhoto(newPhoto);
      
      setSelectedFile(null);
      setNotes('');
      setCategory('front');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error adding photo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPhoto = (photo: PhotoEntry) => {
    setSelectedPhoto(photo);
    setViewDialog(true);
  };

  const handleCompareToggle = (photo: PhotoEntry) => {
    if (compareMode) {
      if (comparePhotos.find(p => p.id === photo.id)) {
        setComparePhotos(comparePhotos.filter(p => p.id !== photo.id));
      } else if (comparePhotos.length < 2) {
        setComparePhotos([...comparePhotos, photo]);
      }
    }
  };

  const handleDeletePhoto = async (photo: PhotoEntry) => {
    if (!onDeletePhoto) return;
    
    const confirmDelete = window.confirm(`Delete photo from ${format(new Date(photo.date), 'MMM dd, yyyy')}?\n\nCategory: ${getCategoryLabel(photo.category)}${photo.notes ? `\nNotes: "${photo.notes}"` : ''}`);
    if (confirmDelete) {
      try {
        // Delete from Blob storage first (if it's a URL)
        const imageUrl = photo.imageUrl || photo.imageData;
        if (imageUrl && !imageUrl.startsWith('data:')) {
          await deleteFromBlob(imageUrl);
        }
        
        // Then delete from database
        await onDeletePhoto(photo.id);
        
        // Close view dialog if the deleted photo was being viewed
        if (selectedPhoto?.id === photo.id) {
          setViewDialog(false);
          setSelectedPhoto(null);
        }
        // Remove from compare photos if it was selected
        if (comparePhotos.find(p => p.id === photo.id)) {
          setComparePhotos(comparePhotos.filter(p => p.id !== photo.id));
        }
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'front': return '#6366f1';
      case 'side': return '#10b981';
      case 'back': return '#f59e0b';
      case 'other': return '#8b5cf6';
      default: return '#6366f1';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'front': return 'Front View';
      case 'side': return 'Side View';
      case 'back': return 'Back View';
      case 'other': return 'Other';
      default: return category;
    }
  };

  const getPhotosByCategory = () => {
    const grouped: { [key: string]: PhotoEntry[] } = {};
    sortedPhotos.forEach(photo => {
      if (!grouped[photo.category]) {
        grouped[photo.category] = [];
      }
      grouped[photo.category].push(photo);
    });
    return grouped;
  };

  const photosByCategory = getPhotosByCategory();

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Photo Progress
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant={compareMode ? "contained" : "outlined"}
            onClick={() => {
              setCompareMode(!compareMode);
              setComparePhotos([]);
            }}
            startIcon={<Compare />}
            color={compareMode ? "primary" : "inherit"}
          >
            {compareMode ? 'Exit Compare' : 'Compare Mode'}
          </Button>
        </Box>
      </Box>

      {compareMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Compare Mode: Click up to 2 photos to compare them side by side.
            {comparePhotos.length > 0 && ` Selected: ${comparePhotos.length}/2`}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Camera size={24} color="#6366f1" />
                <Typography variant="h6">Add Photo</Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as any)}
                    label="Category"
                  >
                    <MenuItem value="front">Front View</MenuItem>
                    <MenuItem value="side">Side View</MenuItem>
                    <MenuItem value="back">Back View</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<Upload />}
                  sx={{ mb: 2, py: 1.5 }}
                >
                  {selectedFile ? selectedFile.name : 'Choose Photo'}
                </Button>

                {selectedFile && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'cover',
                        borderRadius: 8
                      }}
                    />
                  </Box>
                )}

                <TextField
                  fullWidth
                  label="Notes (optional)"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ mb: 3 }}
                  placeholder="Add notes about this photo..."
                />

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={!selectedFile || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? 'Uploading...' : 'Add Photo'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Progress Summary
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      {photos.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Photos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {Object.keys(photosByCategory).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Categories
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>By Category:</Typography>
                {Object.entries(photosByCategory).map(([cat, photoList]) => (
                  <Box key={cat} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Chip
                      size="small"
                      label={getCategoryLabel(cat)}
                      sx={{ 
                        backgroundColor: getCategoryColor(cat),
                        color: 'white'
                      }}
                    />
                    <Typography variant="body2">{photoList.length}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          {compareMode && comparePhotos.length === 2 ? (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Photo Comparison
                </Typography>
                <Grid container spacing={2}>
                  {comparePhotos.map((photo, index) => (
                    <Grid item xs={6} key={photo.id}>
                      <Box>
                        <img
                          src={photo.imageUrl || photo.imageData}
                          alt={`Compare ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 400,
                            objectFit: 'cover',
                            borderRadius: 8
                          }}
                        />
                        <Box mt={1} textAlign="center">
                          <Chip
                            size="small"
                            label={getCategoryLabel(photo.category)}
                            sx={{ 
                              backgroundColor: getCategoryColor(photo.category),
                              color: 'white',
                              mr: 1
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {format(new Date(photo.date), 'MMM dd, yyyy')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setComparePhotos([])}
                    fullWidth
                  >
                    Clear Comparison
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Photo Gallery
                </Typography>
                
                {photos.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <ImageIcon size={48} color="#ccc" />
                    <Typography variant="h6" color="text.secondary" mt={2}>
                      No photos yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start your photo journey by adding your first progress photo!
                    </Typography>
                  </Box>
                ) : (
                  <ImageList cols={3} gap={12}>
                    {sortedPhotos.map((photo) => {
                      const isSelected = comparePhotos.find(p => p.id === photo.id);
                      return (
                        <ImageListItem 
                          key={photo.id}
                          sx={{ 
                            cursor: 'pointer',
                            border: isSelected ? '3px solid #6366f1' : 'none',
                            borderRadius: 1,
                            overflow: 'hidden'
                          }}
                          onClick={() => compareMode ? handleCompareToggle(photo) : handleViewPhoto(photo)}
                        >
                          <img
                            src={photo.imageUrl || photo.imageData}
                            alt={`Progress ${photo.category}`}
                            style={{
                              height: 200,
                              objectFit: 'cover'
                            }}
                            loading="lazy"
                          />
                          <ImageListItemBar
                            title={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                  size="small"
                                  label={getCategoryLabel(photo.category)}
                                  sx={{ 
                                    backgroundColor: getCategoryColor(photo.category),
                                    color: 'white',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </Box>
                            }
                            subtitle={format(new Date(photo.date), 'MMM dd, yyyy')}
                            actionIcon={
                              !compareMode && (
                                <Box display="flex">
                                  <IconButton
                                    sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewPhoto(photo);
                                    }}
                                  >
                                    <Eye />
                                  </IconButton>
                                  {onDeletePhoto && (
                                    <IconButton
                                      sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePhoto(photo);
                                      }}
                                    >
                                      <Trash2 />
                                    </IconButton>
                                  )}
                                </Box>
                              )
                            }
                          />
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Photo View Dialog */}
      <Dialog 
        open={viewDialog} 
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {selectedPhoto && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={getCategoryLabel(selectedPhoto.category)}
                    sx={{ 
                      backgroundColor: getCategoryColor(selectedPhoto.category),
                      color: 'white'
                    }}
                  />
                  <Chip
                    variant="outlined"
                    icon={<Calendar size={14} />}
                    label={format(new Date(selectedPhoto.date), 'MMM dd, yyyy')}
                  />
                </Box>
              </Box>
              
              <img
                src={selectedPhoto.imageUrl || selectedPhoto.imageData}
                alt="Progress photo"
                style={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
              
              {selectedPhoto.notes && (
                <Box mt={2} p={2} sx={{ backgroundColor: '#f8fafc', borderRadius: 1 }}>
                  <Typography variant="body2" fontStyle="italic">
                    "{selectedPhoto.notes}"
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box>
              {selectedPhoto && onDeletePhoto && (
                <Button 
                  onClick={() => handleDeletePhoto(selectedPhoto)}
                  color="error"
                  startIcon={<Trash2 />}
                >
                  Delete Photo
                </Button>
              )}
            </Box>
            <Button onClick={() => setViewDialog(false)}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Compare Mode FAB */}
      {compareMode && comparePhotos.length < 2 && (
        <Tooltip title={`Select ${2 - comparePhotos.length} more photo${2 - comparePhotos.length > 1 ? 's' : ''} to compare`}>
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {comparePhotos.length}/2
            </Typography>
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};